import { Fragment, useMemo } from 'react';
import { DateValue, now, toZoned, ZonedDateTime } from '@internationalized/date';
import { FilterDropdown, NumberPicker } from '@ui';
import { OrSettingsEnum, OrSettingsType, SuggestedSettingsEnum } from '@lp/features/RoutePlanner/types';
import { formatDateTimeStringToTimeZone, truncateToMinutes } from '@/utils/helpers';
import { DateTimeSettingsPopover } from './DateTimeSettingsPopover';
import { SettingsControlShell } from './SettingsControlShell';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';
import { CUSTOM_OR_DEFAULT_OFFSET_HOURS, OR_MAX_OFFSET_HOURS, OR_TIME_LIST_ITEMS, OR_TIME_TYPE_NAME } from './config';
import { getOrRangeListItems, getOrRangeText } from './utils';

type OrTimeSettingsProps = {
  data: OrSettingsType;
  caseOrTime: ZonedDateTime;
  handleSettingsChange: (data: OrSettingsType) => void;
};

const OR_RANGE_LIST_ITEMS = getOrRangeListItems(OR_MAX_OFFSET_HOURS);

const DEFAULT_OFFSET_BY_OR_TYPE: Record<OrSettingsEnum, number> = {
  [OrSettingsEnum.CurrentOr]: 0,
  [OrSettingsEnum.OrRange]: OR_MAX_OFFSET_HOURS,
  [OrSettingsEnum.CustomOr]: CUSTOM_OR_DEFAULT_OFFSET_HOURS,
};

const getCustomOrDefault = (caseOrTime: ZonedDateTime): ZonedDateTime => {
  const currentTime = now(caseOrTime.timeZone);

  return caseOrTime.compare(currentTime) > 0 ? caseOrTime : currentTime.add({ hours: 1 });
};

export const OrTimeSettings = ({ data, caseOrTime, handleSettingsChange }: OrTimeSettingsProps) => {
  const handleOrTimeTypeChange = (keys: string[]) => {
    const selectedType = keys[0] as OrSettingsEnum;

    handleSettingsChange({
      type: SuggestedSettingsEnum.OR,
      value: {
        type: selectedType,
        offset: DEFAULT_OFFSET_BY_OR_TYPE[selectedType],
        selectedOr: selectedType === OrSettingsEnum.CustomOr ? getCustomOrDefault(caseOrTime) : caseOrTime,
      },
    });
  };

  const handleOrRangeChange = (keys: string[]) => {
    const selectedRange = parseFloat(keys[0]);

    handleSettingsChange({
      type: SuggestedSettingsEnum.OR,
      value: {
        type: OrSettingsEnum.OrRange,
        offset: selectedRange,
        selectedOr: caseOrTime,
      },
    });
  };

  const handleCustomOrChange = (dateTime: DateValue, offset: number) => {
    const zonedDateTimeValue = toZoned(dateTime, caseOrTime.timeZone);

    handleSettingsChange({
      type: SuggestedSettingsEnum.OR,
      value: {
        type: OrSettingsEnum.CustomOr,
        offset,
        selectedOr: zonedDateTimeValue,
      },
    });
  };

  const selectedOrString = useMemo(() => {
    const utcString = data.value.selectedOr.toAbsoluteString();

    return formatDateTimeStringToTimeZone(utcString, data.value.selectedOr.timeZone);
  }, [data.value.selectedOr]);

  return (
    <Fragment key={data.type}>
      <FilterDropdown
        placement="top-start"
        title="OR"
        items={OR_TIME_LIST_ITEMS}
        disallowEmptySelection
        renderCustomTrigger={(isOpen) => (
          <SuggestedSelectTrigger title="OR:" value={OR_TIME_TYPE_NAME[data.value.type]} isOpen={isOpen} />
        )}
        selectedKeys={data.value.type ? [data.value.type] : []}
        mode="single"
        onSelectedKeysChange={handleOrTimeTypeChange}
      />
      {data.value.type === OrSettingsEnum.OrRange && (
        <FilterDropdown
          placement="top-start"
          title="OR"
          items={OR_RANGE_LIST_ITEMS}
          disallowEmptySelection
          renderCustomTrigger={(isOpen) => (
            <SuggestedSelectTrigger title="OR Range:" value={getOrRangeText(data.value.offset)} isOpen={isOpen} />
          )}
          selectedKeys={[String(data.value.offset)]}
          mode="single"
          onSelectedKeysChange={handleOrRangeChange}
        />
      )}
      {data.value.type === OrSettingsEnum.CustomOr && (
        <SettingsControlShell>
          <div className="flex items-center gap-1">
            <span className="text-base-5 text-m-sb">Custom OR:</span>
            <span className="text-base-5 text-m-r">{selectedOrString}</span>
            <span className="text-base-5 text-m-r">{`+ ${data.value.offset}h`}</span>
          </div>
          <DateTimeSettingsPopover
            value={data.value.selectedOr}
            timeInputLabel="Custom OR time picker"
            minValue={now(caseOrTime.timeZone)}
            onChange={(dateTime) => handleCustomOrChange(dateTime, data.value.offset)}
            extraContent={
              <div className="flex justify-between items-center gap-2 px-4 pt-1 pb-3">
                <span className="text-l-m">Range</span>
                <NumberPicker
                  minValue={0}
                  maxValue={OR_MAX_OFFSET_HOURS}
                  value={data.value.offset}
                  renderValue={(value) => <span className="text-m-r text-base-5">{`+${value}h`}</span>}
                  onChange={(value) => handleCustomOrChange(truncateToMinutes(data.value.selectedOr), value)}
                />
              </div>
            }
          />
        </SettingsControlShell>
      )}
    </Fragment>
  );
};
